import { useAccount } from 'wagmi'
import { Address, formatUnits } from 'viem'
import { useMemo } from 'react'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { useEulerIndexerEVaults, useVaultUserBalances, getTokenFromMap, useTokensInfo } from '@/domain/euler'
import { useBatchAssetPrices } from '@/domain/euler/oracle/useAssetPrice'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'

export interface EVaultPosition {
  token: Token
  vaultAddress: Address
  vaultName: string
  vaultSymbol: string
  depositAmount: NormalizedUnitNumber
  depositAmountUSD: NormalizedUnitNumber
  supplyAPY: Percentage
  chainId: number
  rowClickOptions: { destination: string }
}

export interface UseEVaultPositionsParams {
  userAddress?: Address
  chainId?: number
  enabled?: boolean
}

export interface UseEVaultPositionsResult {
  positions: EVaultPosition[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch eVault (Euler Earn) positions
 * Uses the same approach as useEVaults - indexer for vault list, useVaultUserBalances for user data
 */
export function useEVaultPositions({
  userAddress,
  chainId = E_EARN_VAULT_CHAIN_ID,
  enabled = true,
}: UseEVaultPositionsParams = {}): UseEVaultPositionsResult {
  const { address: connectedAddress } = useAccount()
  const address = userAddress || connectedAddress

  // Step 1: Get all vaults from Euler indexer (same as useEVaults)
  const { data: indexerVaultsMap, isPending: isPendingIndexer } = useEulerIndexerEVaults(chainId)

  const indexerVaults = useMemo(() => {
    if (!indexerVaultsMap) return []
    return Array.from(indexerVaultsMap.values())
  }, [indexerVaultsMap])

  // Step 2: Get vault addresses and underlying assets for balance fetching
  const vaultAddresses = useMemo(() => indexerVaults.map((v) => v.address), [indexerVaults])
  const underlyingAssets = useMemo(() => indexerVaults.map((v) => v.asset ?? undefined), [indexerVaults])
  const decimals = useMemo(() => indexerVaults.map((v) => v.assetDecimals ?? v.decimals), [indexerVaults])

  // Step 3: Fetch user balances using shared hook (same as useEVaults)
  const { balances: userBalances, isPending: isPendingBalances } = useVaultUserBalances({
    vaultAddresses,
    underlyingAssets,
    decimals,
    userAddress: address,
    chainId,
    enabled: enabled && !!address,
  })

  // Step 4: Get token info for underlying assets
  const underlyingAssetAddresses = useMemo(
    () => indexerVaults.filter((v) => v.asset).map((v) => v.asset as Address),
    [indexerVaults],
  )
  const parallelAssetAddresses = useMemo(
    () => indexerVaults.map((v) => (v.asset as Address | undefined) ?? undefined),
    [indexerVaults],
  )
  const { tokens: indexerTokens, isPending: isPendingTokens } = useTokensInfo(chainId, underlyingAssetAddresses)

  const lendingVaultAddresses = useMemo(() => {
    const addrs: Address[] = []
    for (const v of indexerVaults) {
      if (!v.strategies) continue
      for (const s of v.strategies) {
        if (s.strategy) addrs.push(s.strategy)
      }
    }
    return addrs
  }, [indexerVaults])

  const { assetPriceMap } = useBatchAssetPrices({
    vaultAddresses,
    assetAddresses: parallelAssetAddresses,
    lendingVaultAddresses,
    chainId,
    enabled: indexerVaults.length > 0,
  })

  // Step 5: Build positions from vaults that have user balance > 0
  const positions: EVaultPosition[] = useMemo(() => {
    if (!address || indexerVaults.length === 0) return []

    const result: EVaultPosition[] = []

    for (const vault of indexerVaults) {
      const userBalanceData = userBalances.get(vault.address)
      if (!userBalanceData || userBalanceData.vaultBalance === 0n) continue

      const tokenDecimals = vault.assetDecimals ?? vault.decimals
      const tokenInfo = getTokenFromMap(indexerTokens, vault.asset ?? undefined)
      const tokenSymbol = tokenInfo?.symbol ?? vault.assetSymbol ?? vault.symbol ?? 'UNKNOWN'

      // Get asset price from oracle (same approach as useEVaults)
      const assetPrice = vault.asset ? assetPriceMap.get(vault.asset.toLowerCase()) ?? 0 : 0

      const token = new Token({
        symbol: TokenSymbol(tokenSymbol),
        name: tokenInfo?.name || vault.name || tokenSymbol,
        decimals: tokenDecimals,
        address: vault.asset
          ? CheckedAddress(vault.asset)
          : CheckedAddress('0x0000000000000000000000000000000000000000'),
        unitPriceUsd: assetPrice.toString(),
        logoURI: tokenInfo?.logoURI,
      })

      // Calculate deposit amount from vault shares
      const depositAmount = NormalizedUnitNumber(formatUnits(userBalanceData.depositAssets, tokenDecimals))
      const depositAmountUSD = NormalizedUnitNumber(depositAmount.multipliedBy(assetPrice))

      result.push({
        token,
        vaultAddress: vault.address,
        vaultName: vault.name,
        vaultSymbol: vault.symbol,
        depositAmount,
        depositAmountUSD,
        supplyAPY: Percentage(vault.apyCurrent ? vault.apyCurrent / 100 : 0, true, true),
        chainId,
        rowClickOptions: {
          destination: `/earn/${chainId}/${vault.address}`,
        },
      })
    }

    return result
  }, [address, indexerVaults, userBalances, indexerTokens, assetPriceMap, chainId])

  const isLoading = isPendingIndexer || (!!address && isPendingBalances) || isPendingTokens

  return {
    positions,
    isLoading,
    error: null,
  }
}
